import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { LOGO_COLOR } from "@/lib/brand";
import { useAuth } from "@/lib/auth.jsx";

const navLinks = [
  { to: "/features", label: "Features" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/use-cases", label: "Use cases" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 glass border-b border-brand-borderc/60">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <img src={LOGO_COLOR} alt="DraftVoice" className="h-9 w-9 rounded-lg object-contain bg-brand-navy" />
          <span className="font-bold text-lg tracking-tight text-brand-navy">
            Draft<span className="text-gradient-primary">Voice</span> <span className="text-brand-muted text-sm font-medium align-top">AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                pathname === l.to ? "text-brand-navy bg-brand-surface" : "text-brand-muted hover:text-brand-navy hover:bg-brand-surface/60"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <button
              data-testid="nav-open-app-btn"
              onClick={() => navigate(user.role === "admin" ? "/admin" : "/app")}
              className="btn-primary-gradient rounded-full px-5 py-2 text-sm font-semibold"
            >
              Open App →
            </button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="px-4 py-2 text-sm font-medium text-brand-navy hover:bg-brand-surface rounded-full">
                Login
              </Link>
              <Link to="/signup" data-testid="nav-signup-btn" className="btn-primary-gradient rounded-full px-5 py-2 text-sm font-semibold">
                Start free
              </Link>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden p-2" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-brand-borderc bg-white px-5 py-4 space-y-1">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-brand-text font-medium">
              {l.label}
            </Link>
          ))}
          <div className="pt-3 flex gap-2">
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/app"} className="btn-primary-gradient rounded-full px-5 py-2 text-sm font-semibold w-full text-center">Open app</Link>
            ) : (
              <>
                <Link to="/login" className="flex-1 text-center py-2 border border-brand-borderc rounded-full">Login</Link>
                <Link to="/signup" className="flex-1 text-center py-2 btn-primary-gradient rounded-full">Start free</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
